import AddIcon from "@mui/icons-material/Add";
import * as React from "react";
import { useForm, Controller } from "react-hook-form";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  getCorrespondingGeoType,
  isGeoSpecificType,
  isLatitudeType,
  isLongitudeType,
  MessagePathNode,
} from "@/shared/domain/topics";
import {
  actions,
  MapPanelState,
  useVizDispatch,
} from "@/shared/state/visualization";

import { DataSourceSelector } from "../../controls/DataSourceSelector";

import styles from "./AddPath.module.css";

interface AddMapPathProps {
  state: MapPanelState;
}

interface AddPathFormData {
  latitude: MessagePathNode | null;
  longitude: MessagePathNode | null;
}

export function AddPath({ state }: AddMapPathProps) {
  const vizStateDispatch = useVizDispatch();
  const [isAddingNewPath, setIsAddingNewPath] = React.useState(true);

  const form = useForm<AddPathFormData>({
    defaultValues: {
      latitude: null,
      longitude: null,
    },
    // Validate fields on every change
    mode: "onChange",
  });

  const latitude = form.watch("latitude");
  const longitude = form.watch("longitude");

  const onSubmit = (data: AddPathFormData) => {
    if (data.latitude && data.longitude) {
      // When adding data, we ensure the data is in [lat, lon] pairs
      const pathData = [
        data.latitude.toTopicData(),
        data.longitude.toTopicData(),
      ];
      vizStateDispatch(actions.addPathToMapPanel(state.id, pathData));
      setIsAddingNewPath(false);
      form.reset();
    }
  };

  const handleCancel = () => {
    setIsAddingNewPath(false);
    form.reset();
  };

  function isLatitude(messagePathNode: MessagePathNode) {
    if (longitude) {
      return (
        messagePathNode.topic.id === longitude.topic.id &&
        getCorrespondingGeoType(longitude.data.canonical_data_type).includes(
          messagePathNode.data.canonical_data_type,
        )
      );
    }
    return isLatitudeType(messagePathNode.data.canonical_data_type);
  }

  function isLongitude(messagePathNode: MessagePathNode) {
    if (latitude) {
      return (
        messagePathNode.topic.id === latitude.topic.id &&
        getCorrespondingGeoType(latitude.data.canonical_data_type).includes(
          messagePathNode.data.canonical_data_type,
        )
      );
    }
    return isLongitudeType(messagePathNode.data.canonical_data_type);
  }

  function geoSpecificFirst(a: MessagePathNode, b: MessagePathNode) {
    const aIsGeo = isGeoSpecificType(a.data.canonical_data_type);
    const bIsGeo = isGeoSpecificType(b.data.canonical_data_type);
    if (aIsGeo === bIsGeo) {
      return 0;
    }
    return aIsGeo ? -1 : 1;
  }

  if (isAddingNewPath) {
    return (
      <div className={styles.formContainer}>
        <RobotoTypography variant="overline">New path</RobotoTypography>
        <form
          className={styles.form}
          onSubmit={(e) => {
            void form.handleSubmit(onSubmit)(e);
          }}
        >
          <div>
            <RobotoTypography component={"label"} variant="subtitle2">
              Latitude
            </RobotoTypography>
            <Controller
              name="latitude"
              control={form.control}
              rules={{ required: true }}
              render={({ field }) => (
                <DataSourceSelector
                  onChange={field.onChange}
                  onClear={() => field.onChange(null)}
                  optionFilter={isLatitude}
                  optionSort={geoSpecificFirst}
                />
              )}
            />
          </div>

          <div>
            <RobotoTypography component={"label"} variant="subtitle2">
              Longitude
            </RobotoTypography>
            <Controller
              name="longitude"
              control={form.control}
              rules={{ required: true }}
              render={({ field }) => (
                <DataSourceSelector
                  onChange={field.onChange}
                  onClear={() => field.onChange(null)}
                  optionFilter={isLongitude}
                  optionSort={geoSpecificFirst}
                />
              )}
            />
          </div>

          <div className={styles.positionRight}>
            <RobotoButton
              eventName={"AddMapPathCanceled"}
              onClick={handleCancel}
              size="small"
              type="button"
            >
              Cancel
            </RobotoButton>
            <RobotoButton
              eventName={"AddMapPathCompleted"}
              type="submit"
              variant="contained"
              size="small"
              disabled={!form.formState.isValid}
            >
              Add Path
            </RobotoButton>
          </div>
        </form>
      </div>
    );
  }

  return (
    <RobotoButton
      eventName={"AddMapPathClicked"}
      onClick={() => setIsAddingNewPath(true)}
      startIcon={<AddIcon />}
      size="small"
    >
      Add Path
    </RobotoButton>
  );
}
